import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import collectionRepository from '@/features/collections/repositories/CollectionRepository';
import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import type { Page, PageMetadata } from '@/features/pages/types/page.types';
import { pageKeys } from '@/shared/query/queryKeys';

/**
 * Optimistic editor-content writer, fired per debounced keystroke.
 *
 * Writes the detail cache synchronously so the open page stays fresh, persists
 * through PageRepository in the background, and only invalidates the sidebar
 * list when the write can change what it displays (the page moving to the top).
 * Content edits never invalidate collection structure: the structure renders
 * titles and ordering only.
 */
export function usePageContentMutation() {
  const queryClient = useQueryClient();
  const setIsDirty = usePageStore(state => state.setIsDirty);

  return useCallback(
    (id: string, content: string) => {
      if (!usePageStore.getState().editable) return;

      const detailKey = pageKeys.detail(id);
      const cached = queryClient.getQueryData<Page>(detailKey);
      if (cached && cached.content === content) return;

      const updatedAt = Date.now();
      if (cached) {
        queryClient.setQueryData<Page>(detailKey, { ...cached, content, updatedAt });
      }
      void pageRepository.updatePage(id, { content });
      setIsDirty(true);

      if (!cached || cached.archivedAt) return;

      if (cached.collectionId) {
        void collectionRepository.updateCollection(cached.collectionId, { updatedAt });
        return;
      }

      const metadata = queryClient.getQueryData<PageMetadata[]>(pageKeys.metadata('updatedAt'));
      if (!metadata || metadata[0]?.id !== id) {
        void queryClient.invalidateQueries({ queryKey: pageKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: pageKeys.first('updatedAt') });
      }
    },
    [queryClient, setIsDirty]
  );
}
